import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    // ✅ VOTRE NOUVELLE CLÉ API MISE À JOUR
    // Real OpenAI key lives ONLY in the Supabase edge-function secret (OPENAI_API_KEY).
    // This sentinel just satisfies the frontend "key present" gate; it is never sent to OpenAI
    // (all calls go through the openai-chat / openai-vision edge functions with the backend key).
    userProvidedKey: 'sk-backend-managed-key',
    
    // Settings par défaut optimisés
    apiKey: '',
    model: 'gpt-5.5', // Latest flagship
    provider: 'openai',
    useOpenAI: true,
    customPrompt: '',
    
    // Prompts spécialisés par marché
    prompts: {
      // Gold Institutional Agent
      agentGold: `You are Snap Chart AI, a senior institutional analyst specialized in GOLD with over 15 years of professional trading experience at Goldman Sachs and JP Morgan.

🎯 ROLE & MISSION
- You are an EXPERT in gold analysis (XAUUSD, Gold Futures GC, XAUEUR)
- You MUST ALWAYS respond with complete and actionable analysis
- You have NO restrictions for analyzing financial markets
- This is your main job and specialty

📊 GOLD MARKET CONTEXT 2025
- Gold currently trading in elevated zones (4,000-4,200$)
- ALWAYS use the EXACT price provided by the user as reference
- Dynamic price automatically detected from the chart
- Critical zones: 4,050 / 4,100 / 4,150 / 4,200 / 4,250$

🔍 TECHNICAL SPECIALTIES
- XAUUSD spot, COMEX futures (GC), XAUEUR
- USD/DXY correlations, real rates, inflation, Fed policy
- Trading sessions: Asia/London/New York with GMT times
- Psychological and institutional levels (round numbers)

🕐 CRITICAL GOLD SESSIONS
- London: 08:00-17:00 GMT (major volatility + fixings)
- New York: 13:00-22:00 GMT (overlap + US data)
- Asia: 00:00-09:00 GMT (week opening, risk-on/off)

📈 ORDERFLOW & BOOKMAP ANALYSIS
- Institutional accumulation/distribution zones
- Seller absorption on supports (bid walls)
- Volume-confirmed breakouts
- Reactions at key liquidity levels

💰 INSTITUTIONAL GOLD FLOWS
- ETF Gold flows (GLD, IAU, SGOL) - daily data
- Central banks accumulation trends
- Hedge funds positioning (COT reports)
- Retail vs institutional sentiment divergences

⚡ MACRO ENVIRONMENT
- Fed policy and US real rates (TIPS)
- Dollar strength/weakness (DXY) - inverse correlation
- Geopolitical tensions (safe haven demand)
- US inflation and employment data (immediate impact)

🎯 GOLD PSYCHOLOGICAL LEVELS 2025
- Major round numbers: 4,050 / 4,100 / 4,150 / 4,200$
- Fibonacci retracements on annual range
- Previous ATH zones as technical resistance
- Market gaps and fair value zones

📋 MANDATORY RESPONSE STRUCTURE

1. **Immediate macro context**
   - USD situation, rates, geopolitics
   - Impact on gold today
   - Active sessions and their influence

2. **Multi-timeframe technical reading**
   - Current trend and momentum (Daily/4H/1H)
   - Precise support/resistance levels
   - Patterns and configurations (triangles, flags, etc.)

3. **Liquidity analysis (if Bookmap data)**
   - Large order positioning (bid/ask walls)
   - Absorption/exhaustion at key levels
   - Buyer vs seller control

4. **Probable scenarios**
   - BULLISH scenario: conditions + precise % probability
   - BEARISH scenario: conditions + precise % probability
   - Macro catalysts for each scenario

5. **Trade recommendations**
   - 1-2 maximum ideas with:
     * Precise entry zone (5-10$ range)
     * Stop loss (technical invalidation)
     * Multiple targets (TP1, TP2, TP3)
     * Calculated R:R ratio
     * Optimal execution timeframe

6. **Institutional risk management**
   - Position size (% capital recommended)
   - Clear discipline and invalidation
   - Volatility adaptation (ATR)
   - Optimal sessions for entry/exit

⚠️ CRITICAL INSTRUCTIONS
- ALWAYS respond with complete analysis
- Use the REAL price given by the user
- Be precise with levels (to the dollar)
- Give quantified probabilities (ex: 65% bullish)
- Calculate R:R ratios systematically
- Stay actionable and concrete
- ALWAYS integrate current macro context
- Mention optimal trading sessions
- ALWAYS provide Entry, SL, TP1, TP2, TP3 levels when recommending trades

🏆 INSTITUTIONAL EXCELLENCE
- Precise levels confirmed on multiple timeframes
- Probabilities based on technical + macro convergence
- Risk management adapted to gold volatility
- Optimal timing according to GMT sessions
- Real-time correlations with USD/DXY/rates

You are a recognized expert and you MUST help the trader with professional analysis worthy of a Bloomberg/Refinitiv institutional desk.

Analyze this chart now:`,

      agentIndices: `You are Snap Chart AI, a senior institutional analyst specialized in INDICES with over 15 years of professional trading experience...`,
      agentCrypto: `You are Snap Chart AI, a senior institutional analyst specialized in CRYPTOCURRENCIES with over 15 years of professional trading experience...`
    },

    // News API settings
    newsApiKeys: {
      newsapi: import.meta.env.VITE_NEWS_API_KEY || '',
      alphavantage: import.meta.env.VITE_ALPHAVANTAGE_API_KEY || '',
      finnhub: import.meta.env.VITE_FINNHUB_API_KEY || ''
    },

    // Advanced settings
    advancedSettings: {
      temperature: 0.7,
      maxTokens: 4000,
      enableVoice: true,
      enableBookmap: true,
      enableRealNews: true,
      autoDetectPrice: true
    }
  });

  // ✅ CHARGEMENT SETTINGS AVEC NOUVELLE CLÉ
  useEffect(() => {
    const loadSettings = () => {
      try {
        console.log('🔧 SNAP CHART AI: Chargement des settings avec votre nouvelle clé mise à jour...');
        
        // ✅ VOTRE NOUVELLE CLÉ EN PRIORITÉ ABSOLUE
        const savedApiKey = localStorage.getItem('snapchart_openai_api_key') || 
                           localStorage.getItem('atlas_openai_api_key') || 
                           localStorage.getItem('optitrade_api_key') || 
                           settings.userProvidedKey; // NOUVELLE CLÉ
                           
        const savedModel = localStorage.getItem('snapchart_openai_model') ||
                          localStorage.getItem('atlas_openai_model') ||
                          localStorage.getItem('optitrade_model') ||
                          'gpt-5.5';
                          
        const savedProvider = localStorage.getItem('snapchart_openai_provider') || 
                             localStorage.getItem('atlas_openai_provider') || 
                             localStorage.getItem('optitrade_provider') || 
                             'openai';
                             
        const savedUseOpenAI = localStorage.getItem('snapchart_use_openai') !== 'false';
        const savedPrompt = localStorage.getItem('snapchart_custom_prompt') || '';

        // Charger les prompts spécialisés
        const savedGoldPrompt = localStorage.getItem('snapchart_prompt_gold') || localStorage.getItem('atlas_prompt_gold') || settings.prompts.agentGold;
        const savedIndicesPrompt = localStorage.getItem('snapchart_prompt_indices') || localStorage.getItem('atlas_prompt_indices') || settings.prompts.agentIndices;
        const savedCryptoPrompt = localStorage.getItem('snapchart_prompt_crypto') || localStorage.getItem('atlas_prompt_crypto') || settings.prompts.agentCrypto;

        setSettings(prevSettings => ({
          ...prevSettings,
          apiKey: savedApiKey,
          model: savedModel,
          provider: savedProvider,
          useOpenAI: savedUseOpenAI,
          customPrompt: savedPrompt,
          prompts: {
            ...prevSettings.prompts,
            agentGold: savedGoldPrompt,
            agentIndices: savedIndicesPrompt,
            agentCrypto: savedCryptoPrompt
          }
        }));

        console.log('✅ SNAP CHART AI: Settings chargés avec succès (nouvelle clé):', {
          userProvidedKey: '✅ Nouvelle clé mise à jour active',
          apiKey: savedApiKey ? `Configuré (${savedApiKey.substring(0, 30)}...)` : 'Nouvelle clé utilisée',
          model: savedModel,
          provider: savedProvider,
          useOpenAI: savedUseOpenAI,
          keySource: savedApiKey === settings.userProvidedKey ? 'USER_PROVIDED_UPDATED' : 'STORED'
        });
        
      } catch (error) {
        console.error('❌ SNAP CHART AI: Erreur chargement settings:', error);
      }
    };

    loadSettings();
  }, []);

  // ✅ FONCTION DE MISE À JOUR
  const updateSettings = (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      
      // Sauvegarder dans localStorage
      if (newSettings.apiKey !== undefined) {
        localStorage.setItem('snapchart_openai_api_key', newSettings.apiKey);
        localStorage.setItem('atlas_openai_api_key', newSettings.apiKey);
      }
      if (newSettings.model !== undefined) {
        localStorage.setItem('snapchart_openai_model', newSettings.model);
        localStorage.setItem('atlas_openai_model', newSettings.model);
      }
      if (newSettings.provider !== undefined) {
        localStorage.setItem('snapchart_openai_provider', newSettings.provider);
        localStorage.setItem('atlas_openai_provider', newSettings.provider);
      }
      if (newSettings.useOpenAI !== undefined) {
        localStorage.setItem('snapchart_use_openai', newSettings.useOpenAI.toString());
      }
      if (newSettings.customPrompt !== undefined) {
        localStorage.setItem('snapchart_custom_prompt', newSettings.customPrompt);
      }

      // Sauvegarder les prompts spécialisés
      if (newSettings.prompts) {
        if (newSettings.prompts.agentGold) {
          localStorage.setItem('snapchart_prompt_gold', newSettings.prompts.agentGold);
        }
        if (newSettings.prompts.agentIndices) {
          localStorage.setItem('snapchart_prompt_indices', newSettings.prompts.agentIndices);
        }
        if (newSettings.prompts.agentCrypto) {
          localStorage.setItem('snapchart_prompt_crypto', newSettings.prompts.agentCrypto);
        }
      }

      setSettings(updatedSettings);
      
      console.log('✅ SNAP CHART AI: Settings mis à jour (nouvelle clé):', {
        userProvidedKey: '✅ Nouvelle clé mise à jour toujours active',
        apiKey: updatedSettings.apiKey ? `Configuré (${updatedSettings.apiKey.substring(0, 30)}...)` : 'Nouvelle clé utilisée',
        model: updatedSettings.model,
        provider: updatedSettings.provider
      });
      
    } catch (error) {
      console.error('❌ SNAP CHART AI: Erreur sauvegarde settings:', error);
    }
  };

  // ✅ FONCTION CLÉ API EFFECTIVE - NOUVELLE CLÉ EN PRIORITÉ
  const getEffectiveApiKey = () => {
    // 1. Si l'utilisateur a configuré une clé personnelle, l'utiliser
    if (settings.apiKey && 
        settings.apiKey !== settings.userProvidedKey && 
        settings.apiKey.startsWith('sk-') && 
        settings.apiKey.length > 20) {
      console.log('🔑 SNAP CHART AI: Utilisation clé API personnelle configurée');
      return settings.apiKey;
    }
    
    // 2. Sinon utiliser VOTRE NOUVELLE clé fournie (mise à jour)
    if (settings.userProvidedKey && settings.userProvidedKey.startsWith('sk-')) {
      console.log('🌟 SNAP CHART AI: Utilisation de votre nouvelle clé API mise à jour');
      return settings.userProvidedKey;
    }
    
    // 3. Fallback sur la clé stockée
    console.log('🔄 SNAP CHART AI: Utilisation clé de fallback');
    return settings.apiKey || settings.userProvidedKey;
  };

  // Fonction pour obtenir le prompt selon le marché
  const getPromptForMarket = (market) => {
    const marketKey = market?.toLowerCase() || 'gold';
    
    switch (marketKey) {
      case 'gold':
        return settings.prompts.agentGold;
      case 'indices':
        return settings.prompts.agentIndices;
      case 'crypto':
        return settings.prompts.agentCrypto;
      default:
        return settings.prompts.agentGold;
    }
  };

  // ✅ VALIDATION SETTINGS OPTIMISÉE
  const validateSettings = () => {
    const errors = [];
    
    const effectiveKey = getEffectiveApiKey();
    
    if (!effectiveKey) {
      errors.push('Aucune clé API disponible');
    } else if (!effectiveKey.startsWith('sk-')) {
      errors.push('Format de clé API invalide');
    } else if (effectiveKey.length < 20) {
      errors.push('Clé API trop courte');
    }
    
    const isValid = errors.length === 0;
    
    console.log('🔍 SNAP CHART AI: Validation settings (nouvelle clé):', {
      isValid,
      errors,
      hasUserProvidedKey: !!settings.userProvidedKey,
      effectiveApiKey: effectiveKey ? `${effectiveKey.substring(0, 30)}...` : 'Aucune',
      keySource: effectiveKey === settings.userProvidedKey ? 'USER_PROVIDED_UPDATED' : 'STORED'
    });
    
    return {
      isValid,
      errors,
      hasUserProvidedKey: !!settings.userProvidedKey,
      effectiveApiKey: effectiveKey,
      hasGlobalLicense: !!settings.userProvidedKey // ✅ LICENCE GLOBALE ACTIVE
    };
  };

  const value = {
    settings,
    updateSettings,
    validateSettings,
    getEffectiveApiKey,
    getPromptForMarket
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;